"use client";

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@aichat/ui/lib/utils";

type SliderProps = React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Root
> & {
  trackClassname?: string;
  thumbClassname?: string;
  rangeClassname?: string;
  indicatorClassname?: string;
  showIndicators?: boolean;
};

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      trackClassname,
      rangeClassname,
      thumbClassname,
      indicatorClassname,
      showIndicators,
      ...props
    },
    ref,
  ) => {
    const totalStepIndicators = React.useMemo(() => {
      if (!props.max || !props.step) return 0;

      return props.max / props.step;
    }, [props.max, props.step]);
    return (
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          className,
        )}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            "relative h-2 w-full grow overflow-hidden rounded-full bg-secondary",
            trackClassname,
          )}
        >
          {showIndicators && (
            <div className="flex items-center justify-between bg-transparent size-full">
              {Array.from({ length: totalStepIndicators + 1 }).map((_, idx) => {
                return (
                  <div
                    key={idx}
                    className={cn(
                      "bg-white rounded-full size-[4px]",
                      indicatorClassname,
                      // Don't show first and last indicators as they are at the end of track
                      // This does not look good
                      idx === 0 || idx === totalStepIndicators
                        ? "opacity-0"
                        : "opacity-100",
                    )}
                  />
                );
              })}
            </div>
          )}
          <SliderPrimitive.Range
            className={cn("absolute h-full top-0 bg-primary", rangeClassname)}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className={cn(
            "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
            thumbClassname,
          )}
        />
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
